import React, { useMemo, useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';
import { useDataGridProTable, MuiXDataGridSurface } from '../hooks/useDataGridProTable';
import {
  getMuiNativeTableDenseLayout,
  MUI_NATIVE_TABLE_OUTER_SX,
} from '../constants/muiNativeTableDenseLayout';
import { useGlobalDenseMode } from '../hooks/useGlobalDenseMode';
import { endpoints } from '../api/config';
import { useCachedData } from '../hooks/useCachedData';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DIALOG_CLOSE_BUTTON_STYLE } from '../constants/styles';

const ShipErrors = () => {
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const defaults = {
      customer_name: true,
      transaction_id: true,
      item: true,
      unique_id: true,
      request_payload: true,
      api_error_code: true,
      api_response: true,
    };
    const saved = localStorage.getItem('ship-errors-column-visibility');
    if (!saved) return defaults;
    const parsed = JSON.parse(saved);
    return {
      ...parsed,
      ...defaults,
    };
  });

  // First level nested dialog states (api_response, request_payload)
  const [nestedDialogOpen, setNestedDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // View dialog for api_response/request_payload
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewDialogTitle, setViewDialogTitle] = useState('');
  const [viewDialogData, setViewDialogData] = useState(null);
  
  // Second level nested dialog states (errors array)
  const [errorsDialogOpen, setErrorsDialogOpen] = useState(false);
  const [errorsData, setErrorsData] = useState([]);

  const { denseMode } = useGlobalDenseMode();
  const nativeDense = useMemo(() => getMuiNativeTableDenseLayout(denseMode), [denseMode]);
  const tableActionTextSx = {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '0.75rem',
    fontWeight: 700,
    color: 'primary.main',
    cursor: 'pointer',
    userSelect: 'none',
    textDecoration: 'none',
    textUnderlineOffset: '2px',
    '&:hover': { color: 'primary.dark', textDecoration: 'underline' },
  };

  const { data: rawData, isLoading, error, refetch } = useCachedData(endpoints.shippingLabelErrors, {
    refetchInterval: 30000,
    staleTime: 20000,
  });

  // Process data to show only top-level fields in main table
  const data = useMemo(() => {
    if (!rawData) return [];
    if (Array.isArray(rawData)) return rawData;
    if (typeof rawData === 'object') {
      const rows = [];
      Object.values(rawData).forEach((v) => {
        if (Array.isArray(v)) {
          v.forEach((item) => rows.push(item));
        } else if (v && typeof v === 'object') {
          rows.push(v);
        }
      });
      return rows.length ? rows : [rawData];
    }
    return [];
  }, [rawData]);

  // Add small preview fields so large objects don't force huge column widths
  const tableData = useMemo(() => {
    return (Array.isArray(data) ? data : []).map(row => ({
      ...row,
      request_payload_preview: row?.request_payload ? 'View' : '-',
      api_response_preview: row?.api_response ? 'View' : '-',
      api_error_code: (() => {
        try {
          const apiResp = row?.api_response;
          if (!apiResp) return '-';
          const errs = apiResp?.data?.errors || apiResp?.errors || apiResp?.error || null;
          if (Array.isArray(errs) && errs.length > 0) return errs[0]?.code || errs[0]?.error_code || errs[0]?.errorCode || '-';
          if (typeof apiResp === 'string') {
            try {
              const parsed = JSON.parse(apiResp);
              const perrs = parsed?.data?.errors || parsed?.errors;
              if (Array.isArray(perrs) && perrs.length > 0) return perrs[0]?.code || perrs[0]?.error_code || perrs[0]?.errorCode || '-';
            } catch (e) {
              return '-';
            }
          }
          return '-';
        } catch (e) {
          return '-';
        }
      })(),
      api_error_description: (() => {
        try {
          const apiResp = row?.api_response;
          if (!apiResp) return '-';
          const errs = apiResp?.data?.errors || apiResp?.errors || apiResp?.error || null;
          if (Array.isArray(errs) && errs.length > 0) return errs[0]?.description || errs[0]?.message || String(errs[0]);
          if (typeof apiResp === 'object' && apiResp?.message) return apiResp.message;
          if (typeof apiResp === 'string') {
            try {
              const parsed = JSON.parse(apiResp);
              const perrs = parsed?.data?.errors || parsed?.errors;
              if (Array.isArray(perrs) && perrs.length > 0) return perrs[0]?.description || perrs[0]?.message || String(perrs[0]);
              if (parsed?.message) return parsed.message;
            } catch (e) {
              return String(apiResp);
            }
          }
          return '-';
        } catch (e) {
          return '-';
        }
      })(),
    }));
  }, [data]);

  // Save column visibility to localStorage
  useEffect(() => {
    localStorage.setItem('ship-errors-column-visibility', JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  // Handle row click to show nested data
  const handleRowClick = useCallback((row) => {
    setSelectedRow(row);
    setNestedDialogOpen(true);
  }, []);

  const handleViewNested = useCallback((title, data) => {
    setViewDialogTitle(title);
    setViewDialogData(data || null);
    setViewDialogOpen(true);
  }, []);

  // Handle errors array click
  const handleViewErrors = useCallback((errors) => {
    setErrorsData(Array.isArray(errors) ? errors : []);
    setErrorsDialogOpen(true);
  }, []);

  // Define columns for the main table
  const columns = useMemo(
    () => [
      {
        accessorKey: 'customer_name',
        header: 'Customer Name',
        size: 150,
      },
      {
        accessorKey: 'transaction_id',
        header: 'Transaction ID',
        size: 120,
      },
      {
        accessorKey: 'item',
        header: 'Item',
        size: 80,
      },
      {
        accessorKey: 'api_error_code',
        header: 'Code',
        size: 120,
        cell: ({ row }) => {
          const apiResp = row.original?.api_response;
          let code = '-';
          try {
            if (apiResp) {
              const errs = apiResp?.data?.errors || apiResp?.errors || apiResp?.error || null;
              if (Array.isArray(errs) && errs.length > 0) {
                code = errs[0]?.code || errs[0]?.error_code || errs[0]?.errorCode || '-';
              } else if (typeof apiResp === 'string') {
                try {
                  const parsed = JSON.parse(apiResp);
                  const perrs = parsed?.data?.errors || parsed?.errors;
                  if (Array.isArray(perrs) && perrs.length > 0) {
                    code = perrs[0]?.code || perrs[0]?.error_code || perrs[0]?.errorCode || '-';
                  }
                } catch (e) {
                  code = '-';
                }
              }
            }
          } catch (e) {
            code = '-';
          }

          return (
            <Typography variant="body2" component="span" sx={{ display: 'block', whiteSpace: 'nowrap' }}>
              {code || '-'}
            </Typography>
          );
        }
      },
      {
        accessorKey: 'error_message',
        header: 'Error',
        size: 200,
        cell: ({ row }) => {
          const desc = row.original?.error_message || '-';

          return (
            <Typography
              variant="body2"
              component="span"
              sx={{ display: 'block', whiteSpace: 'nowrap' }}
              title={desc}
            >
              {desc || '-'}
            </Typography>
          );
        }
      },
      {
        accessorKey: 'request_payload_preview',
        header: 'Request Payload',
        size: 100,
        cell: ({ row }) => (
          <Box
            component="span"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              handleViewNested('Request Payload', row.original?.request_payload || null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                handleViewNested('Request Payload', row.original?.request_payload || null);
              }
            }}
            sx={tableActionTextSx}
          >
            View
          </Box>
        )
      },
      {
        accessorKey: 'api_response_preview',
        header: 'API Response',
        size: 100,
        cell: ({ row }) => {
          const value = row.original?.api_response;
          if (value === null || value === undefined || value === '') {
            return '-';
          }
          return (
            <Box
              component="span"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                handleViewNested('API Response', row.original?.api_response || null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleViewNested('API Response', row.original?.api_response || null);
                }
              }}
              sx={tableActionTextSx}
            >
              View
            </Box>
          );
        }
      },
      {
        accessorKey: 'unique_id',
        header: 'Unique ID',
        size: 120,
      },
    ],
    [handleViewNested, tableActionTextSx]
  );

  const appGrid = useDataGridProTable({
    title: 'Ship Errors',
    columns,
    data: tableData || [],
    loading: isLoading,
    error: error ? error.message || 'Failed to load ship errors' : null,
    onRefetch: refetch,
    initialColumnVisibility: columnVisibility,
    onColumnVisibilityChange: setColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    searchOnEnter: true,
    onRowClick: handleRowClick,
    muiTableContainerProps: { sx: { height: '100%', minHeight: 0 } },
  });

  const renderRowDetails = (row) => {
    if (!row || typeof row !== 'object') {
      return (
        <Typography variant="body2" sx={{ p: 2 }}>
          {String(row || 'N/A')}
        </Typography>
      );
    }

    const entries = Object.entries(row).filter(
      ([key]) => key !== 'api_response' && key !== 'request_payload'
    );

    return (
      <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
        <Table size={nativeDense.tableSize} stickyHeader sx={MUI_NATIVE_TABLE_OUTER_SX}>
          <TableHead>
            <TableRow sx={nativeDense.headerRowSx}>
              <TableCell sx={{ ...nativeDense.headerCellSx, fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '30%' }}>Key</TableCell>
              <TableCell sx={{ ...nativeDense.headerCellSx, fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map(([key, value]) => (
              <TableRow key={key} hover sx={nativeDense.bodyRowSx}>
                <TableCell sx={{ fontWeight: 500 }}>{key}</TableCell>
                <TableCell>
                  {typeof value === 'object' && value !== null ? (
                    <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {JSON.stringify(value, null, 2)}
                    </pre>
                  ) : (
                    String(value ?? '-')
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Render nested object as key-value table
  const renderNestedObject = (obj) => {
    if (!obj || typeof obj !== 'object') {
      return (
        <Typography variant="body2" sx={{ p: 2 }}>
          {String(obj || 'N/A')}
        </Typography>
      );
    }

    const entries = Object.entries(obj);
    
    return (
      <TableContainer component={Paper} sx={{ maxHeight: 400, mb: 2 }}>
        <Table size={nativeDense.tableSize} stickyHeader sx={MUI_NATIVE_TABLE_OUTER_SX}>
          <TableHead>
            <TableRow sx={nativeDense.headerRowSx}>
              <TableCell sx={{ ...nativeDense.headerCellSx, fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '30%' }}>Key</TableCell>
              <TableCell sx={{ ...nativeDense.headerCellSx, fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map(([key, value]) => (
              <TableRow key={key} hover sx={nativeDense.bodyRowSx}>
                <TableCell sx={{ fontWeight: 500 }}>{key}</TableCell>
                <TableCell>
                  {Array.isArray(value) && key === 'errors' ? (
                    <Typography
                      component="span"
                      sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={() => handleViewErrors(value)}
                    >
                      View Errors ({value.length})
                    </Typography>
                  ) : typeof value === 'object' && value !== null ? (
                    <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {JSON.stringify(value, null, 2)}
                    </pre>
                  ) : (
                    String(value ?? '-')
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <DashboardLayout>
      <Box sx={{ p: 1.5, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1.5, fontSize: '1.25rem' }}>
          Ship Errors
        </Typography>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <MuiXDataGridSurface hook={appGrid} DataGridPro={DataGridPro} />
        </Box>
      </Box>

      {/* Row Details Dialog - Excludes API Response and Request Payload */}
      <Dialog
        open={nestedDialogOpen}
        onClose={() => setNestedDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            width: '90%',
            maxWidth: '1200px',
            height: '85vh',
            maxHeight: '85vh'
          }
        }}
      >
        <DialogTitle sx={{ p: 2, pb: 1.5, pr: 10, position: 'relative' }}>
          <Typography component="span" sx={{ fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.6 }}>
            Ship Error Details - {selectedRow?.transaction_id || 'N/A'}
          </Typography>
          <IconButton
            aria-label="close"
            size="small"
            onClick={() => setNestedDialogOpen(false)}
            sx={DIALOG_CLOSE_BUTTON_STYLE}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          {selectedRow ? renderRowDetails(selectedRow) : null}
        </DialogContent>
      </Dialog>

      {/* View Dialog for API Response / Request Payload */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            width: '90%',
            maxWidth: '1200px',
            height: '85vh',
            maxHeight: '85vh'
          }
        }}
      >
        <DialogTitle sx={{ p: 2, pb: 1.5, pr: 10, position: 'relative' }}>
          <Typography component="span" sx={{ fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.6 }}>
            {viewDialogTitle || 'Details'}
          </Typography>
          <IconButton
            aria-label="close"
            size="small"
            onClick={() => setViewDialogOpen(false)}
            sx={DIALOG_CLOSE_BUTTON_STYLE}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          {viewDialogData ? (
            <Box>
              {viewDialogTitle === 'API Response' && viewDialogData?.data && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Data</Typography>
                  {renderNestedObject(viewDialogData.data)}
                </Box>
              )}
              {viewDialogTitle === 'API Response' && viewDialogData?.meta && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Meta</Typography>
                  {renderNestedObject(viewDialogData.meta)}
                </Box>
              )}
              {viewDialogTitle === 'Request Payload' && (
                <Box>
                  {viewDialogData?.action && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Action:</strong> {viewDialogData.action}
                    </Typography>
                  )}
                  {viewDialogData?.carrier && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Carrier:</strong> {viewDialogData.carrier}
                    </Typography>
                  )}
                  {viewDialogData?.params && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Parameters</Typography>
                      {renderNestedObject(viewDialogData.params)}
                    </Box>
                  )}
                </Box>
              )}
              {viewDialogTitle !== 'API Response' && viewDialogTitle !== 'Request Payload' && renderNestedObject(viewDialogData)}
            </Box>
          ) : (
            <Typography variant="body2">No data available</Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Second Level Nested Dialog - Errors Array */}
      <Dialog
        open={errorsDialogOpen}
        onClose={() => setErrorsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            width: '80%',
            maxWidth: '800px',
            height: '70vh',
            maxHeight: '70vh'
          }
        }}
      >
        <DialogTitle sx={{ p: 2, pb: 1.5, pr: 10, position: 'relative' }}>
          <Typography component="span" sx={{ fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.6 }}>
            Error Details
          </Typography>
          <IconButton
            aria-label="close"
            size="small"
            onClick={() => setErrorsDialogOpen(false)}
            sx={DIALOG_CLOSE_BUTTON_STYLE}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          {errorsData.length > 0 ? (
            <TableContainer component={Paper}>
              <Table size={nativeDense.tableSize} sx={MUI_NATIVE_TABLE_OUTER_SX}>
                <TableHead>
                  <TableRow sx={nativeDense.headerRowSx}>
                    <TableCell sx={{ ...nativeDense.headerCellSx, fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Code</TableCell>
                    <TableCell sx={{ ...nativeDense.headerCellSx, fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Type</TableCell>
                    <TableCell sx={{ ...nativeDense.headerCellSx, fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {errorsData.map((error, idx) => (
                    <TableRow key={idx} hover sx={nativeDense.bodyRowSx}>
                      <TableCell>{error.code || '-'}</TableCell>
                      <TableCell>{error.type || '-'}</TableCell>
                      <TableCell>{error.description || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2">No errors available</Typography>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default ShipErrors;
